import { config } from "@nova/configuration/browser"
import { getCookie } from "cookies-next"
import { SHA256 } from "crypto-js"
import { now } from "nodash"
import { NextApiRequest } from "next"
import { isNonEmptyString, isValidString } from "tools/strings/isNonEmptyString"

const systemCookieId = "fireAi"
let credentialsCookiePrefix = undefined as string | undefined

/**
 * spells out and throws accordingly when the prefix cannot be generated
 * it is a hard to detect problem, that sneaks upon us in deployed environments, and can easily be missed in localhost. therefore, making it very obvious here!
 *
 * @param systemId string
 * @param applicationId string - we use the awsUserPoolWebClientId as our unique identifier
 * @returns string
 * @throws when missing systemId or applicationId
 */
function makeCredentialsCookiePrefix(systemId: string, applicationId: string) {
    if (!isValidString(systemId)) {
        throw "Received invalid system id. Unable to make credentialsCookiePrefix"
    }

    if (!isValidString(applicationId)) {
        console.warn("Encountered a fatal error. Review your environment configuration:", {})
        throw "Received invalid application id. Unable to make credentialsCookiePrefix"
    }

    return `${systemId}.${applicationId}`
}

export function getClientApplicationId() {
    return SHA256(config.get("awsUserPoolWebClientId")).toString()
}

/**
 * this covers both server and client cases. problem here is the "publicEnvironment" values cannot be set in client given our build-once-deploy-to-multi environments setup
 * for a good overview why the intuitive approach doesn't work:
 * - https://github.com/vercel/next.js/issues/40827#issuecomment-1255804642
 * - https://www.saltycrane.com/blog/2021/04/buildtime-vs-runtime-environment-variables-nextjs-docker/#using-runtime-environment-variables-client-side-or-server-side
 *
 * instead, the client SystemsManager fires a request prior to showing the app, and once that completes, we get the id which is stored in the redux store
 *
 * Could we use the publicRuntimeConfig? maybe. but it ties us very closely into the nextjs eco system, is unreliable and not very transparent, and most of the time didn't work.
 * Hence, this approach will be more robust in the long-term.
 *
 * @returns string
 */
export function getCredentialsCookiePrefix() {
    if (isNonEmptyString(credentialsCookiePrefix)) {
        return credentialsCookiePrefix
    }

    const id = getClientApplicationId()
    if (isValidString(id)) {
        credentialsCookiePrefix = makeCredentialsCookiePrefix(systemCookieId, id)
    }

    return credentialsCookiePrefix
}

export const lastUsedCookieName = () => `${getCredentialsCookiePrefix()}.lastUsed`
export const accessTokenCookieName = (subject: string) =>
    `${getCredentialsCookiePrefix()}.${subject}.accessToken`
export const idTokenCookieName = (subject: string) =>
    `${getCredentialsCookiePrefix()}.${subject}.idTokenCookie`
export const refreshTokenCookieName = (subject: string) =>
    `${getCredentialsCookiePrefix()}.${subject}.refreshTokenCookie`
export const refreshExpiresCookieName = (subject: string) =>
    `${getCredentialsCookiePrefix()}.${subject}.refreshExpires`
export const expiresCookieName = (subject: string) =>
    `${getCredentialsCookiePrefix()}.${subject}.expires`
export const registrationTokenCookieName = () =>
    `${getCredentialsCookiePrefix()}.registrationCookie`
export const challengeSessionTokenCookieName = () =>
    `${getCredentialsCookiePrefix()}.challengeCookie`
export const challengeNameCookieName = () => `${getCredentialsCookiePrefix()}.challengeName`
export const deviceKeyCookieName = (subject: string) => `device-key-${subject}`

/**
 * uses gets the credential cookie value for the one that indicates which user (subject) was last authenticated
 *
 * @param options - ResolverEnvironmentOptions - MUST be provided if using this method on backend, else the cookies cannot be retrieved
 * @returns string | undefined
 */
export function getLastAuthenticatedSubject(options?: { req: NextApiRequest }): string | undefined {
    const name = lastUsedCookieName()
    const value = getCookie(name, options)

    if (typeof value === "string" && value !== "") {
        return value
    }

    return undefined
}

// if a token expires in (T) seconds, then our buffer will indicated at (T - buffer) that the expires is not "valid" anymore
// in theory that will provide us with the ability to avoid rejected requests and reauthenticate early whenever we can
const defaultExpiryBufferInSeconds = 600

/**
 * takes an expires value (usually from cookie) and tries to determine if it is valid (not expired) given the buffer
 * @param expires
 * @param expiryBufferInSeconds
 * @returns boolean
 */
export function validateExpires(
    expires: boolean | number | string | null | undefined,
    expiryBufferInSeconds = defaultExpiryBufferInSeconds
) {
    if (!(typeof expires === "string" || typeof expires === "number")) {
        return false
    }

    // for some reason aws sends these in seconds. Divide milliseconds by 1000 and you get seconds
    const nowInSeconds = Math.floor(now() / 1000)
    const expiresInSeconds = parseInt(expires as string)

    if (!Number.isInteger(expiresInSeconds)) {
        return false
    }

    return expiresInSeconds < nowInSeconds + expiryBufferInSeconds
}

/**
 *
 * @param options ResolverEnvironmentOptions (req/res if in BFF-environment)
 * @returns boolean
 */
export function hasAuthenticationCookies(options?: { req: NextApiRequest }): boolean {
    const subject = getLastAuthenticatedSubject(options)

    // unable to lookup credentials without this
    if (!subject) {
        return false
    }

    const accessToken = getCookie(accessTokenCookieName(subject), options)
    const refreshExpires = getCookie(refreshExpiresCookieName(subject), options)
    const expires = getCookie(expiresCookieName(subject), options)
    const accessTokenIsExpired = validateExpires(expires)
    const refreshTokenIsExpired = validateExpires(refreshExpires)

    if ((accessToken && !accessTokenIsExpired) || !refreshTokenIsExpired) {
        return true
    }

    return false
}

/**
 * looks at the current request and tries to get the associated accessToken
 * will return an empty string, instead of throwing, if no access token exists
 *
 * @returns string
 */
export function getActiveAccessToken(options?: { req: NextApiRequest }): string {
    const subject = getLastAuthenticatedSubject(options)
    if (!subject) {
        return ""
    }

    const accessToken = getCookie(accessTokenCookieName(subject), options)
    if (typeof accessToken !== "string") {
        return ""
    }

    return accessToken
}
